/**
 * MERODE edge markers as a React SVG <defs> block.
 *
 * This is the React Flow port of `src/lib/joint/markers.js` (the JointJS source
 * of truth). The path data, stroke widths, fills and refX/refY are copied
 * verbatim from there; only the rendering mechanism changes (JointJS `V('marker',
 * ...)` -> JSX <marker>). Markers are referenced by string id via the SVG
 * `marker-start` / `marker-end` properties, exactly as the old links did
 * (`url(#merode-marker-...)`), so every id below matches a JointJS id 1:1.
 *
 * Five base markers:
 *  - optional         : hollow circle (white fill, black stroke)
 *  - mandatory        : filled circle
 *  - many             : filled triangle / arrow head (also the transition end)
 *  - inherit          : hollow triangle
 *  - inherit-abstract : filled triangle
 *
 * Each base marker also has four highlight colour variants (suffix on the id):
 *  -highlighted (#FFAC2C), -highlighted-path (#DC2376),
 *  -primary-path (#34d18a), -secondary-path (#f85359).
 * => 5 x 5 = 25 markers.
 *
 * Colour is BAKED into each marker (not driven by CSS): IE11 ignored CSS `fill`
 * on markers, and React Flow's PNG export (html-to-image) likewise can't rely on
 * external stylesheet cascade into a cloned subtree. The edge components pick the
 * right variant id for the link's highlight state.
 *
 * markerUnits is `userSpaceOnUse` (per the phase-6 plan): the glyph keeps a fixed
 * pixel size regardless of edge stroke width, matching the JointJS visual. Each
 * marker therefore carries an explicit viewBox + markerWidth/markerHeight (1:1
 * with user space) so it is not clipped to the SVG default 3x3 marker box.
 *
 * orient: the JointJS markers used `orient="auto"`. SVG rotates an `auto` marker
 * to follow the path direction, but a marker-START is then rotated 180deg from
 * what a human expects (it points back along the path). MERODE edges put these
 * markers on the link SOURCE (marker-start), so we expose an `orient` prop and
 * the gallery / edge code can pass `auto-start-reverse` for marker-start usage.
 * Default stays `auto` to match marker-end usage and the JointJS default.
 */
// The app-wide Vite esbuild JSX transform is the CLASSIC runtime
// (`react({ jsxRuntime: 'classic' })`), which emits React.createElement, so JSX
// files must have React in scope — import it explicitly like the rest of the
// codebase. (tsconfig keeps jsx:'react-jsx' for type-checking only; the import
// is harmless there and required at runtime.)
import React from 'react';

// Marker ids, id maps and variant metadata live in the pure-data sibling so this
// component file exports only components (the Fast Refresh boundary).
import {
  OPTIONAL,
  MANDATORY,
  MANY,
  INHERIT,
  INHERIT_ABSTRACT,
  VARIANTS,
  MARKER_VARIANTS,
  type VariantKey,
  type MarkerNames,
} from './markerIds';

// --- geometry (verbatim from src/lib/joint/markers.js) ----------------------

// A circle as an SVG path (radius 7.5, centred on the marker ref point).
const CIRCLE_PATH = 'm -7.5, 0 a 7.5,7.5 0 1,0 15,0 a 7.5,7.5 0 1,0 -15,0 z';
// A triangle / arrow head.
const TRIANGLE_PATH = 'M 0 0 L 7 5 L 0 10 L 10 5 z';
// The inheritance triangle.
const INHERIT_TRIANGLE_PATH = 'M 0 10 L 20 20 L 20 0 z';

type MarkerShape = {
  d: string;
  strokeWidth: number;
  refX: number;
  refY: number;
  // Explicit marker box so userSpaceOnUse renders the glyph 1:1 without clipping.
  viewBox: string;
  size: number; // markerWidth === markerHeight
  // `hollow` markers keep a white fill in every variant (only the stroke recolours);
  // `solid` markers recolour both stroke and fill.
  hollow: boolean;
};

const SHAPES = {
  optional: {
    d: CIRCLE_PATH,
    strokeWidth: 2,
    refX: 0,
    refY: 0,
    viewBox: '-10 -10 20 20',
    size: 20,
    hollow: true,
  },
  mandatory: {
    d: CIRCLE_PATH,
    strokeWidth: 2,
    refX: 0,
    refY: 0,
    viewBox: '-10 -10 20 20',
    size: 20,
    hollow: false,
  },
  many: {
    d: TRIANGLE_PATH,
    strokeWidth: 1,
    refX: 10,
    refY: 5,
    viewBox: '-2 -2 14 14',
    size: 14,
    hollow: false,
  },
  inherit: {
    d: INHERIT_TRIANGLE_PATH,
    strokeWidth: 1,
    refX: 0,
    refY: 10,
    viewBox: '-2 -2 24 24',
    size: 24,
    hollow: true,
  },
  inheritAbstract: {
    d: INHERIT_TRIANGLE_PATH,
    strokeWidth: 1,
    refX: 0,
    refY: 10,
    viewBox: '-2 -2 24 24',
    size: 24,
    hollow: false,
  },
} satisfies Record<string, MarkerShape>;

type MarkerOrient = 'auto' | 'auto-start-reverse';

type SingleMarkerProps = {
  id: string;
  shape: MarkerShape;
  /** Stroke colour (default black). */
  color?: string;
  orient: MarkerOrient;
};

function Marker({ id, shape, color = 'black', orient }: SingleMarkerProps) {
  const fill = shape.hollow ? 'white' : color;
  return (
    <marker
      id={id}
      markerUnits="userSpaceOnUse"
      markerWidth={shape.size}
      markerHeight={shape.size}
      viewBox={shape.viewBox}
      refX={shape.refX}
      refY={shape.refY}
      orient={orient}
      overflow="visible"
    >
      <path d={shape.d} strokeWidth={shape.strokeWidth} stroke={color} fill={fill} />
    </marker>
  );
}

/** The five base markers, in definition order, with their ids. */
const BASE: { id: string; shape: MarkerShape }[] = [
  { id: OPTIONAL, shape: SHAPES.optional },
  { id: MANDATORY, shape: SHAPES.mandatory },
  { id: MANY, shape: SHAPES.many },
  { id: INHERIT, shape: SHAPES.inherit },
  { id: INHERIT_ABSTRACT, shape: SHAPES.inheritAbstract },
];

const VARIANT_SHAPE_KEYS: (keyof MarkerNames)[] = [
  'OPTIONAL',
  'MANDATORY',
  'MANY',
  'INHERIT',
  'INHERIT_ABSTRACT',
];

const VARIANT_SHAPES = [
  SHAPES.optional,
  SHAPES.mandatory,
  SHAPES.many,
  SHAPES.inherit,
  SHAPES.inheritAbstract,
];

export type MerodeMarkerDefsProps = {
  /**
   * `orient` for every marker. Use the default `auto` for marker-END usage
   * (matches JointJS). Pass `auto-start-reverse` if these defs feed marker-START
   * (MERODE puts cardinality markers on the link source) so the glyph points
   * outward instead of back along the path.
   */
  orient?: MarkerOrient;
};

/**
 * Renders all 25 MERODE markers inside an SVG <defs>. Mount this ONCE inside the
 * subtree that React Flow exports to PNG (the captured viewport) so the markers
 * survive html-to-image's clone of that subtree, then reference any id with
 * `markerStart={`url(#${id})`}` / `markerEnd`.
 */
export function MerodeMarkerDefs({ orient = 'auto' }: MerodeMarkerDefsProps) {
  return (
    <defs>
      {BASE.map(({ id, shape }) => (
        <Marker key={id} id={id} shape={shape} orient={orient} />
      ))}
      {(Object.entries(VARIANTS) as [VariantKey, { color: string }][]).map(([key, { color }]) =>
        VARIANT_SHAPE_KEYS.map((shapeKey, i) => (
          <Marker
            key={MARKER_VARIANTS[key][shapeKey]}
            id={MARKER_VARIANTS[key][shapeKey]}
            shape={VARIANT_SHAPES[i]}
            color={color}
            orient={orient}
          />
        ))
      )}
    </defs>
  );
}

export default MerodeMarkerDefs;
